// §9.6 / §18 F10 — unloading a `resumable` app, and putting it back.
//
// "`resumable: true` permits the shell to unload the iframe after 15 minutes minimized and rebuild
// it from the app's `session` blob. `resumable: false` (Flightdeck) is never unloaded."
//
// Three things this module is careful about:
//
//  1. **`resumable: false` is a promise, not a preference.** Flightdeck holds an edit session that
//     no blob can reconstruct, so nothing here may ever unload one — the manifest flag is checked
//     before the clock is even read.
//  2. **Unloading is not closing.** The window stays, the `.win-host` stays (§5.7), the bar entry
//     stays, and the session blob stays. What goes is the *iframe*, which is the WebGL context and
//     the memory the unload exists to reclaim.
//  3. **The clock is a parameter.** `sweep(at)` is a pure function of the time it is handed, so a
//     test — or an e2e — fast-forwards by passing a later number rather than by waiting fifteen
//     minutes or by monkey-patching `Date`.
//
// The DOM half lives in the host layer (§6.5: the kernel cannot reach it). This decides *what*
// should happen and says so on the bus; `Desktop.tsx` is what destroys and rebuilds a frame.
import type { Win } from '@hangar/shared';
import type { Bus } from '../bus';

/** §9.6 — fifteen minutes minimized. */
export const RESUMABLE_IDLE_MS = 15 * 60 * 1000;

/** How often the shell looks. A minute is far below the resolution of a fifteen-minute rule. */
export const RESUME_SWEEP_MS = 60 * 1000;

export interface ResumeService {
  /**
   * Unload every resumable window that has been minimized since `at - idleMs`. Returns the
   * instance keys unloaded, and emits `app:unloaded` for each.
   */
  sweep(at: number): string[];
  /** Put an unloaded window back. Emits `app:resumed`; the host rebuilds the frame. */
  resume(instanceKey: string): boolean;
  isUnloaded(instanceKey: string): boolean;
  unloaded(): string[];
  /** When this window was minimized, for App Inspector and the tests. */
  minimizedSince(instanceKey: string): number | null;
  /** Subscribe to the bus and start the sweep. Returns the detach. */
  attach(): () => void;
}

export interface ResumeOptions {
  bus: Bus;
  /** Every open window, at the moment of asking. */
  windows: () => readonly Win[];
  /** §9.6 — does this app's manifest permit an unload? A built-in never does: there is no frame. */
  resumable: (appId: string) => boolean;
  now?: () => number;
  idleMs?: number;
  /** Injected so a test does not need a real timer. Returns the cancel. */
  schedule?: (fn: () => void, everyMs: number) => () => void;
}

function defaultSchedule(fn: () => void, everyMs: number): () => void {
  const timer = setInterval(fn, everyMs);
  return () => clearInterval(timer);
}

export function createResumeService(options: ResumeOptions): ResumeService {
  const idleMs = options.idleMs ?? RESUMABLE_IDLE_MS;
  const now = options.now ?? (() => Date.now());
  const schedule = options.schedule ?? defaultSchedule;

  /** When each minimized window went down. A window not in here is not minimized. */
  const minimizedAt = new Map<string, number>();
  /** Windows whose frame is gone. They are still open, still in the bar, still in the session. */
  const unloadedKeys = new Set<string>();

  const winById = (id: string): Win | undefined => options.windows().find((win) => win.id === id);

  const service: ResumeService = {
    sweep(at) {
      const done: string[] = [];
      for (const win of options.windows()) {
        if (unloadedKeys.has(win.instanceKey)) continue;
        // The flag first, always. A `resumable: false` app is not a slow case of a resumable one.
        if (!options.resumable(win.appId)) continue;
        const since = minimizedAt.get(win.instanceKey);
        if (since === undefined || at - since < idleMs) continue;

        minimizedAt.delete(win.instanceKey);
        unloadedKeys.add(win.instanceKey);
        done.push(win.instanceKey);
        options.bus.emit('app:unloaded', { instanceKey: win.instanceKey, appId: win.appId });
      }
      return done;
    },

    resume(instanceKey) {
      if (!unloadedKeys.delete(instanceKey)) return false;
      const win = options.windows().find((candidate) => candidate.instanceKey === instanceKey);
      if (!win) return false;
      options.bus.emit('app:resumed', { instanceKey, appId: win.appId });
      return true;
    },

    isUnloaded: (instanceKey) => unloadedKeys.has(instanceKey),
    unloaded: () => [...unloadedKeys],
    minimizedSince: (instanceKey) => minimizedAt.get(instanceKey) ?? null,

    attach() {
      const off = [
        options.bus.on('window:stateChanged', ({ id, state }) => {
          const win = winById(id);
          if (!win) return;
          if (state === 'minimized') {
            // Only the *first* minimize starts the clock; re-announcing it must not restart it.
            if (!minimizedAt.has(win.instanceKey)) minimizedAt.set(win.instanceKey, now());
            return;
          }
          minimizedAt.delete(win.instanceKey);
          // F10 — "restored from its session blob on next focus". Coming out of the bar is that.
          service.resume(win.instanceKey);
        }),

        options.bus.on('window:focused', ({ id }) => {
          if (id === null) return;
          const win = winById(id);
          if (win) service.resume(win.instanceKey);
        }),

        options.bus.on('window:closed', ({ instanceKey }) => {
          minimizedAt.delete(instanceKey);
          unloadedKeys.delete(instanceKey);
        }),
      ];

      const cancel = schedule(() => service.sweep(now()), RESUME_SWEEP_MS);

      return () => {
        cancel();
        for (const unsubscribe of off) unsubscribe();
      };
    },
  };

  return service;
}
